import { Dispatch, SetStateAction } from 'react';
import { Badge } from 'components/ui/badge';
import { Switch } from 'components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/ui/tooltip';
import { ColumnConfig } from 'components/table';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import { PROCESSOR_NAMES } from 'utils/constants';
import { isProcessorType } from 'utils/processor';
import { ProcessorTableData } from 'types/settings/internalConfig';
import { ProcessorActionButton } from 'components/settings/internalConfig/ProcessorActionButton';
import { MerchantIdInput } from 'components/settings/internalConfig/MerchantIdInput';

interface GetColumnsProps {
  deletedProcessors: Set<string>;
  tooltipStates: { [key: string]: boolean };
  setTooltipStates: Dispatch<SetStateAction<{ [key: string]: boolean }>>;
  merchantIds: { [processorId: string]: string };
  setMerchantIds: Dispatch<SetStateAction<{ [processorId: string]: string }>>;
  handleToggle: (row: PAYMENT_CONFIG.IProcessors) => Promise<void>;
  handleDelete: (row: PAYMENT_CONFIG.IProcessors) => Promise<void>;
  handleActivate: (row: PAYMENT_CONFIG.IProcessors) => Promise<void>;
  togglingProcessorId: string | null;
}

export const getColumns = ({
  deletedProcessors,
  tooltipStates,
  setTooltipStates,
  merchantIds,
  setMerchantIds,
  handleToggle,
  handleDelete,
  handleActivate,
  togglingProcessorId,
}: GetColumnsProps): ColumnConfig<ProcessorTableData>[] => [
  {
    key: 'processorName',
    label: 'Payment Provider',
    sortable: true,
    renderCell: (row) => <div className="capitalize">{row.processorName}</div>,
  },
  {
    key: '_paymentMethod',
    label: 'Payment Solution',
    sortable: true,
    renderCell: (row) => (
      <div className="capitalize">{`Integrated ${row._paymentMethod}`}</div>
    ),
  },
  {
    key: '_status',
    label: 'Status',
    sortable: true,
    renderCell: (row) => {
      const isDeleted = deletedProcessors.has(row.processorId);
      if (isDeleted || row.isEnabled === null) {
        return <Badge variant="progress">Needs Activation</Badge>;
      } else if (row.isEnabled) {
        return <Badge variant="info">Enabled</Badge>;
      } else {
        return <Badge variant="secondary">Disabled</Badge>;
      }
    },
  },
  {
    key: '_enableDisable',
    renderHeader: () => <div className="text-center">Enable/Disable</div>,
    renderCell: (row) => {
      const isDeleted = deletedProcessors.has(row.processorId);
      const isNotConfigured = row.isEnabled === null;

      // Disabled state with tooltip
      if (isDeleted || isNotConfigured) {
        return (
          <div className="flex items-center justify-center gap-3">
            <TooltipProvider>
              <Tooltip open={tooltipStates[row.processorId] || false}>
                <TooltipTrigger asChild>
                  <div
                    className="opacity-50 cursor-help"
                    onMouseEnter={() => {
                      setTooltipStates((prev) => ({
                        ...prev,
                        [row.processorId]: true,
                      }));
                    }}
                    onMouseLeave={() => {
                      setTooltipStates((prev) => ({
                        ...prev,
                        [row.processorId]: false,
                      }));
                    }}
                  >
                    <Switch className="mt-1" disabled checked={false} />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {isDeleted
                    ? "Processor is deleted. Use 'Activate' to restore it."
                    : 'Processor needs activation before it can be enabled.'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        );
      }

      // Active toggle with merchant ID inputs
      const isAeropay = isProcessorType(
        row?.processorName,
        PROCESSOR_NAMES.AEROPAY
      );
      const isBarterpay = isProcessorType(
        row?.processorName,
        PROCESSOR_NAMES.BARTERPAY
      );
      const showMerchantInput =
        !isDeleted && (isAeropay || isBarterpay) && row.isEnabled === false;

      return (
        <div className="flex items-center justify-center gap-3">
          <Switch
            className="mt-1"
            id={`${row.processorId}`}
            checked={row.isEnabled}
            disabled={
              togglingProcessorId === row.processorId ||
              ((isAeropay || isBarterpay) &&
                !row.isEnabled &&
                !(merchantIds?.[row.processorId] || '').trim())
            }
            onCheckedChange={() => handleToggle(row)}
          />

          {showMerchantInput && (
            <MerchantIdInput
              processorId={row.processorId}
              processorName={row.processorName}
              value={merchantIds?.[row.processorId] || ''}
              onChange={(value) =>
                setMerchantIds((prev) => ({
                  ...prev,
                  [row.processorId]: value,
                }))
              }
            />
          )}
        </div>
      );
    },
  },
  {
    key: '_actions',
    renderHeader: () => <div className="text-center">Actions</div>,
    renderCell: (row) => {
      const isDeleted = deletedProcessors.has(row.processorId);

      return (
        <div className="flex items-center justify-center">
          <ProcessorActionButton
            isEnabled={isDeleted ? null : row.isEnabled}
            isDeleted={isDeleted}
            onDelete={() => handleDelete(row)}
            onActivate={() => handleActivate(row)}
          />
        </div>
      );
    },
  },
];
